import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import apiConfig from "constants/apiConfig"
import { CollaborationTasksSummary, MilestoneData } from "types/campaign/collaborations"
import { fetchCollaborationChatRoom, fetchCollaborationMilestoneData, fetchCollaborationTasksSummaryThunk } from "./thunk"
import { CollaborationChatRoom } from "types/campaign/collaborations/chat"

export interface CollaborationMilestoneSlice {
    loading: boolean,
    error: string | null,
    collaborations: Record<string, //CampaignId
        Record<string, // collaborationId
            Record<string, // MilestoneId
                {
                    data: MilestoneData
                    last_fetched_at: number,
                    expired: boolean
                }
            >
        >
    >,
    chatRooms: Record<string, //CampaignId
        Record<string, // InfluencerId
            {
                room: CollaborationChatRoom
                last_fetched_at: number,
            }
        >
    >,
    summary: {
        data: CollaborationTasksSummary,
        last_fetched_at: number | null,
        loading: boolean,
        error: string | null
    },
}

const initialState: CollaborationMilestoneSlice = {
    loading: true,
    error: null,
    collaborations: {},
    chatRooms: {},
    summary: {
        data: {
            steps: {}
        },
        last_fetched_at: null,
        loading: true,
        error: null
    }
}



const collaborationMilestones = createSlice({
    name: 'collaborationMilestones',
    initialState,
    reducers: {
        resetTasksSummary: (state) => {
            state.summary.last_fetched_at = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCollaborationMilestoneData.pending, (state) => {
                state.error = null;
                state.loading = true;
            })
            .addCase(fetchCollaborationMilestoneData.fulfilled, (state, { payload }) => {
                const { campaignId, milestoneId, collaborationId, data, cache } = payload;

                state.collaborations[campaignId] = {
                    ...(state.collaborations[campaignId] ?? {}),
                    [collaborationId]: {
                        ...(state.collaborations[campaignId]?.[collaborationId] ?? {}),
                        [milestoneId]: {
                            ...(state.collaborations[campaignId]?.[collaborationId]?.[milestoneId] ?? {}),
                            data,
                            expired: false,
                            ...(!cache ? { last_fetched_at: Date.now() } : {})
                        }
                    }
                }

                state.loading = false;
                state.error = null;
            })
            .addCase(fetchCollaborationTasksSummaryThunk.pending, (state) => {
                state.summary.loading = true;
                state.summary.error = null;
            })
            .addCase(fetchCollaborationTasksSummaryThunk.fulfilled, (state, { payload }) => {
                state.summary = {
                    data: payload.summary,
                    last_fetched_at: Date.now(),
                    loading: false,
                    error: null
                }
            })
            .addCase(fetchCollaborationTasksSummaryThunk.rejected, (state) => {
                state.summary.loading = false;
            })
            .addCase(fetchCollaborationChatRoom.fulfilled, (state, {payload}) => {
                const { cache, campaignId, collaborationId, room } = payload;
                if(cache) return;
                state.chatRooms[campaignId] = {
                    ...(state.chatRooms[campaignId] ?? {}),
                    [collaborationId] : {
                        room,
                        last_fetched_at: Date.now()
                    }
                }
            })
    }
})

export const { resetTasksSummary } = collaborationMilestones.actions;

export default collaborationMilestones.reducer
